import { defineComponent as _defineComponent } from 'vue'
import { nextTick, onMounted, ref, watch } from 'vue';
import type { PropType } from 'vue';
import { notification, Tooltip } from 'ant-design-vue';
import { ProductRowItem } from '@/interfaces/general/ProductRowItem';
import { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import { ConfiguratorValue } from '@/interfaces/components/configurator/ConfiguratorValue';
import { UpdateProductFormEntry } from '@/interfaces/components/configurator/UpdateProductFormEntry';
import ChecklistBoolFieldModule from '@/components/views/productView/ChecklistBoolFieldModule.vue';
import ChecklistDropdownFieldModule from '@/components/views/productView/ChecklistDropdownFieldModule.vue';
import ChecklistFloatFieldModule from '@/components/views/productView/ChecklistFloatFieldModule.vue';
import ChecklistIntegerFieldModule from '@/components/views/productView/ChecklistIntegerFieldModule.vue';
import ChecklistStringFieldModule from '@/components/views/productView/ChecklistStringFieldModule.vue';
import { determineFieldVisibility } from '@/helpers/FieldFunctionHelper';
import { ChecklistField } from '@/interfaces/components/configurator/ChecklistField';
import Offer from '@/models/Offer';
import { filterProductsFromOfferItems as filterOfferItems } from '@/helpers/OfferItemTableHelper';
import OfferItem from '@/models/OfferItem';
import OfferTitle from '@/models/OfferTitle';
import { TransformedTableOfferItem } from '@/interfaces/components/ProductsInOffer/TransformedTableOfferItem';
// @ts-ignore
import is from 'is_js';
import { setMultipositionRowElementPositioning } from '@/helpers/FormDataHelper';
import RichTextEditor from '@/components/views/CmsIndex/RichTextEditor.vue';
import store from '@/store';

/* === Props & Emits === */

export default /*#__PURE__*/_defineComponent({
  __name: 'MultipositionHeader',
  props: {
    productData: { type: Object as PropType<ProductRowItem>, required: true },
    activeItemRows: { type: Array as PropType<ProductRowItem[]>, required: true },
    hiddenIndices: { type: Array as PropType<boolean[]>, required: true },
    isLoading: { type: Boolean, default: false },
    isCustomProduct: { type: Boolean, default: false },
    offer: { type: Object as PropType<Offer | null>, default: null },
    level: { type: Number, required: false },
    numberOfProductForms: { type: Number, default: 1 },
    productDetails: { required: false, default: false },
    checklistSegmentName: { type: String, required: false },
},
  emits: ['is-connected', 'is-production'],
  setup(__props, { expose, emit }) {

const props = __props;





/* === State & Store Access === */
const wrapper = ref<HTMLElement | null>(null);
const imageWrapper = ref<HTMLElement | null>(null);
const text = ref<any>(null);
const isModalVisible = ref(false);
const modalChecklistField = ref<ChecklistField | null>(null);
const isMultipositionHeaderFrozen = ref(false);

expose({ wrapper });

const updateActiveProductFormValue = (entry: UpdateProductFormEntry) =>
    store.dispatch('configurator/updateActiveProductFormValue', entry);
const getProductFormValues = (productFormId: number): ActiveProductFormValueObject | null =>
    store.getters['configurator/activeProductForm'](productFormId);

/* === Field Rendering & Modal === */
function activeFieldModule(checklistFieldType: string) {
    switch (checklistFieldType) {
        case 'float':
            return ChecklistFloatFieldModule;
        case 'string':
            return ChecklistStringFieldModule;
        case 'boolean':
            return ChecklistBoolFieldModule;
        case 'integer':
            return ChecklistIntegerFieldModule;
        case 'dropdown':
            return ChecklistDropdownFieldModule;
    }
}

function isVisible(checklistField: ChecklistField, index: number) {
    return (
        (props.hiddenIndices.length > 0 ? !props.hiddenIndices[index] : false) && checklistField.level <= props.level
    );
}

function toggleInfoDetailsModal(checklistField: ChecklistField | null) {
    if (checklistField && checklistField.description == null) {
        return;
    }
    isModalVisible.value = !isModalVisible.value;
    modalChecklistField.value = isModalVisible.value ? checklistField : null;

    if (isModalVisible.value && is.safari()) {
        nextTick(() => {
            const modalInner = document.querySelector('.ant-modal-body') as HTMLElement;
            const textHeight = text.value.getBoundingClientRect().height;
            const modalInnerHeight = modalInner.getBoundingClientRect().height;

            imageWrapper.value.style.height = modalInnerHeight - textHeight - 110 + 'px';
        });
    }
}

function filterProductsFromOfferItems(offerItems: Array<OfferItem | OfferTitle | TransformedTableOfferItem>) {
    return filterOfferItems(offerItems, false);
}

/* === Field Synchronization === */
function onUpdateStoreFieldValues({ pId, value }: { pId: string; value: ConfiguratorValue }) {
    nextTick(() => {
        notification.destroy();
    });
    for (const activeItemRow of props.activeItemRows) {
        const productFormValues = getProductFormValues(activeItemRow.activeProductFormId);
        const editableField = activeItemRow.editableFields.find((fieldToBeEdited: ChecklistField) => {
            return fieldToBeEdited.pId === pId;
        });
        if (productFormValues == null || editableField == null) {
            continue;
        }
        if (!determineFieldVisibility(editableField.checklistVisibleFunction, productFormValues)) {
            continue;
        }
        updateActiveProductFormValue({
            pId,
            value,
            productFormId: activeItemRow.activeProductFormId,
        });
    }
}

/* === Sticky Column Positioning === */
function setWatcherForRowUpdateAndFixCss() {
    const unwatch = watch(
        () => props.isLoading,
        async (newVal) => {
            if (newVal) return;

            setTimeout(() => {
                const rowElement = document.getElementById(
                    `multiposition-header${props.productData.activeProductFormId}`,
                );

                if (!rowElement) {
                    return;
                }
                const elements = rowElement.getElementsByClassName(
                    'is-multiposition--visible',
                ) as HTMLCollectionOf<HTMLElement>;

                if (elements.length === 0) {
                    return;
                }

                const systemElement = rowElement
                    .querySelector('[data-test-id="p7405"]')
                    ?.closest('.is-multiposition') as HTMLElement;

                const widthElement = rowElement
                    .querySelector('[data-test-id="p7402"]')
                    ?.closest('.is-multiposition') as HTMLElement;

                const heightElement = rowElement
                    .querySelector('[data-test-id="p7403"]')
                    ?.closest('.is-multiposition') as HTMLElement;

                // Fix css by adjusting each element of the first 5 elements by the distance they are from the first leftmost element
                if (
                    !systemElement.classList.contains('is-multiposition--visible') ||
                    !widthElement.classList.contains('is-multiposition--visible') ||
                    !heightElement.classList.contains('is-multiposition--visible')
                ) {
                    const rowWrapper = rowElement.nextElementSibling as HTMLElement;
                    const rows = rowWrapper.getElementsByClassName(
                        'c-add-product-row__wrapper',
                    ) as HTMLCollectionOf<HTMLElement>;

                    if (rows.length) {
                        for (let i = 0; i < rows.length; i++) {
                            setMultipositionRowElementPositioning(rows[i]);
                        }
                    }

                    let rowLeft = 112; // Reset for each row
                    for (let i = 0; i < 5; i++) {
                        const element = elements[i];

                        if (i === 0) {
                            element.style.left = '112px';
                        } else if (element) {
                            element.style.left = `${rowLeft}px`;
                        }

                        rowLeft += element?.offsetWidth || 0; // Adjust margin as needed
                    }
                }
                unwatch();
            }, 700);
        },
        { immediate: false },
    );
}

onMounted(() => {
    setWatcherForRowUpdateAndFixCss();
});

return { __sfc: true,props, emit, wrapper, imageWrapper, text, isModalVisible, modalChecklistField, isMultipositionHeaderFrozen, updateActiveProductFormValue, getProductFormValues, activeFieldModule, isVisible, toggleInfoDetailsModal, filterProductsFromOfferItems, onUpdateStoreFieldValues, setWatcherForRowUpdateAndFixCss, Tooltip, RichTextEditor }
}

})